import {
  DialogActions,
  DialogContent,
  FormHelperText,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useFormik } from 'formik';
import { PracticeDraftBEStep } from 'practice/constants/practice-management-step';
import { memo, useEffect } from 'react';
import { toast } from 'react-toastify';
import { PmiButton } from 'shell/components/shared/button';
import { PmiDialogTitle } from 'shell/components/shared/dialog/title';
import { PmiInputFile } from 'shell/components/shared/file-input';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { Attachment } from 'shell/models/attachment';
import { HttpResponse } from 'shell/models/http-response';
import { AttachmentsData } from 'shell/models/practice';
import { readFileAsBase64 } from 'shell/utils/file-to-base64';
import { noop } from 'shell/utils/noop';
import * as yup from 'yup';
import { PmiDialog } from './error-modal';

// tipo allegato "Modulo di domanda compilato"
const ID_TIPO_ALLEGATO_DOMANDA = 2;

interface AttachRequestVM {
  file?: File;
}

interface AttachRequestModalProps {
  open: boolean;
  onClose: () => void;
  codeEnte?: string;
  yearId?: string;
  typeRegister?: string;
  progressive?: string;
}

const AttachRequestModalComponent = ({
  open,
  onClose,
  codeEnte,
  yearId,
  typeRegister,
  progressive,
}: AttachRequestModalProps): React.ReactElement => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  // sovrascrive l'eventuale domanda già allegata alla pratica
  const overwriteAttachment = useHttpClient(
    `${__PRACTICE_API_URL__}/pratiche/allegati/sovrascrivi`,
    {
      options: { method: 'POST' },
    }
  );

  const formContext = useFormik<AttachRequestVM>({
    initialValues: {
      file: undefined,
    },
    validationSchema: yup.object().shape({
      file: yup
        .mixed()
        .required('Il campo è obbligatorio')
        .test(
          'file-type',
          'Il file deve essere un PDF o un’immagine',
          (value) =>
            !value ||
            (value as File).type?.includes('pdf') ||
            (value as File).type?.startsWith('image/')
        ),
    }),
    onSubmit: async (values) => {
      const file = values.file as File;

      const fileBase64 = await readFileAsBase64(file);

      const data: Attachment = {
        codiceEnte: codeEnte || undefined,
        tipoRegistro: typeRegister || undefined,
        idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
        progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
        nomeFile: file.name || undefined,
        idTipoAllegato: ID_TIPO_ALLEGATO_DOMANDA,
        idTipoFormato: file.type?.includes('pdf') ? 1 : 99,
        contenutoAllegato: fileBase64 as string,
        tipoOrigine: 'C',
        stepBozza: PracticeDraftBEStep.Recap,
      };

      // la promise restituita mantiene isSubmitting attivo fino alla risposta
      return (overwriteAttachment({ data }) as ResponsePromise<HttpResponse<AttachmentsData>>)
        .then(() => {
          toast.success('Operazione eseguita con successo');
          onClose();
        })
        .catch(noop);
    },
  });

  // reset del form ad ogni apertura
  useEffect(() => {
    if (open) {
      formContext.resetForm();
    }
  }, [open]);

  return (
    <PmiDialog
      fullScreen={fullScreen}
      open={open}
      onClose={onClose}
      aria-labelledby="attach-request-dialog-title"
    >
      <PmiDialogTitle id="attach-request-dialog-title" backgroundColor="cdmBrand" color="white">
        Allega domanda
      </PmiDialogTitle>
      <DialogContent style={{ padding: '30px 24px' }}>
        <div className="pmi-pb-2">
          <Typography>
            Selezionare il modulo di domanda compilato. L&apos;eventuale domanda già allegata verrà
            sovrascritta.
          </Typography>
        </div>
        Modulo di domanda compilato *&nbsp;
        <PmiInputFile
          id="allega-domanda-upload"
          file={formContext.values.file}
          onChange={(file) => formContext.setFieldValue('file', file)}
          color="cdmBrandThird"
          multiple={false}
          accept="application/pdf, image/*"
        />
        {formContext.touched.file && formContext.errors.file ? (
          <FormHelperText error color="cdmBrand">
            {formContext.errors.file}
          </FormHelperText>
        ) : undefined}
      </DialogContent>
      <DialogActions>
        <PmiButton variant="outlined" color="cdmBrand" type="button" onClick={onClose}>
          Annulla
        </PmiButton>
        <PmiButton
          variant="outlined"
          color="cdmBrandThird"
          type="button"
          disabled={formContext.isSubmitting}
          onClick={() => formContext.handleSubmit()}
        >
          Conferma
        </PmiButton>
      </DialogActions>
    </PmiDialog>
  );
};

AttachRequestModalComponent.displayName = 'Modal attach request';

const AttachRequestModal = memo(AttachRequestModalComponent);

export default AttachRequestModal;
